import { Elements, CardExpiryElement, CardCvcElement, useElements, CardNumberElement } from '@stripe/react-stripe-js';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useCallback, useContext, useEffect, useRef, useState, type RefObject } from 'react';
import type {
  StripeCardNumberElement,
  StripeCardExpiryElement,
  StripeElementLocale,
  StripeCardCvcElement,
} from '@stripe/stripe-js';
import { inputStyles } from '@/styles/input';
import { cn } from '@/utils/cn';
import { CardBrandIcons } from '../PaymentInputs/CardBrandIcons';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { Email } from '../Common/Email';
import { shouldHideEmail } from '@/utils/conditionalRendering';
import { getLocale } from '@/utils/get-locale-util';
import { STRIPE_APPEARANCE } from '@/constants/stripe';
import { useTranslate } from '@/hooks/useTranslate';
import { getStripeErrorMessage } from '@/utils/stripe';
import { PaymentMethodEnum } from '@/types/common';
import { Loader } from '../Common/Loader';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { isEmailValid } from '@/utils/email';
import isSafari from '@/utils/safari';

interface NewCardFormProps {
  cardElementRef: RefObject<StripeCardNumberElement | null>;
}

const NewCardFormContent = ({ cardElementRef }: NewCardFormProps) => {
  const elements = useElements();
  const {
    customer,
    featureFlags,
    actions: { setIsFormValid },
    card: { setCardElementRef },
  } = useContext(CheckoutContext);
  const { t } = useTranslate();
  const [cardNumberValid, setCardNumberValid] = useState(false);
  const [cardExpiryValid, setCardExpiryValid] = useState(false);
  const [cardCvcValid, setCardCvcValid] = useState(false);
  const [cardNumberError, setCardNumberError] = useState<string | null>(null);
  const [cardExpiryError, setCardExpiryError] = useState<string | null>(null);
  const [cardCvcError, setCardCvcError] = useState<string | null>(null);
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  const activeFieldRef = useRef<string | null>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const expiryElementRef = useRef<StripeCardExpiryElement | null>(null);
  const cvcElementRef = useRef<StripeCardCvcElement | null>(null);

  useEffect(() => {
    if (!elements || !cardElementRef) return;

    const cardElement = elements.getElement(CardNumberElement);
    if (cardElement) {
      (cardElementRef as React.MutableRefObject<StripeCardNumberElement | null>).current = cardElement;
      setCardElementRef(cardElementRef);
    }
  }, [elements, cardElementRef, setCardElementRef]);

  useEffect(() => {
    const isFormValid =
      cardNumberValid &&
      cardExpiryValid &&
      cardCvcValid &&
      isZipCodeValid &&
      isEmailValid(customer?.email) &&
      !cardNumberError &&
      !cardExpiryError &&
      !cardCvcError;
    setIsFormValid(isFormValid);
  }, [
    cardNumberValid,
    cardExpiryValid,
    cardCvcValid,
    isZipCodeValid,
    cardNumberError,
    cardExpiryError,
    cardCvcError,
    customer?.email,
    setIsFormValid,
  ]);

  const onTaxValidationChange = useCallback((isValid: boolean) => {
    setIsZipCodeValid(isValid);
  }, []);

  return (
    <div className="flex flex-col items-center w-full gap-3">
      {!shouldHideEmail(featureFlags) && (
        <div className="w-full">
          <Email />
        </div>
      )}
      <div className="flex flex-col gap-1 w-full">
        <div className="relative w-full">
          <div
            className={cn(inputStyles, 'w-full p-0 pl-2', cardNumberError && 'border-red-500')}
            data-testid="stripe-card-number-wrapper"
          >
            <CardNumberElement
              options={{
                iconStyle: 'default',
                disableLink: true,
                style: { base: { fontSize: '14px', padding: '8px 12px', lineHeight: '44px' } },
              }}
              onChange={(e) => {
                setCardNumberValid(e.complete);
                setCardNumberError(getStripeErrorMessage(t, e.error || null, 'number'));
                if (e.complete) {
                  if (isSafari()) {
                    expiryElementRef.current?.focus();
                  } else {
                    setTimeout(() => expiryElementRef.current?.focus(), 0);
                  }
                }
              }}
              onFocus={() => {
                if (blurTimeoutRef.current) {
                  clearTimeout(blurTimeoutRef.current);
                  blurTimeoutRef.current = null;
                }
                activeFieldRef.current = 'number';
              }}
              onBlur={() => {
                // Delay clearing active field to allow focus transition to next field
                blurTimeoutRef.current = setTimeout(() => {
                  if (activeFieldRef.current === 'number') {
                    activeFieldRef.current = null;
                  }
                }, 100);
              }}
            />
          </div>
          <CardBrandIcons />
        </div>
        {cardNumberError && <div className="text-xs text-red-500">{cardNumberError}</div>}
      </div>
      <div className="flex items-start w-full gap-4">
        <div className="flex flex-col gap-1 flex-1">
          <div
            className={cn(inputStyles, 'w-full p-0 pl-2', cardExpiryError && 'border-red-500')}
            data-testid="stripe-card-expiry-wrapper"
          >
            <CardExpiryElement
              options={{ style: { base: { fontSize: '14px', padding: '8px 12px', lineHeight: '44px' } } }}
              onReady={(el) => {
                expiryElementRef.current = el;
              }}
              onChange={(e) => {
                setCardExpiryValid(e.complete);
                setCardExpiryError(getStripeErrorMessage(t, e.error || null, 'expiry'));
                if (e.complete) {
                  setTimeout(() => cvcElementRef.current?.focus(), 0);
                }
              }}
              onFocus={() => {
                if (blurTimeoutRef.current) {
                  clearTimeout(blurTimeoutRef.current);
                  blurTimeoutRef.current = null;
                }
                activeFieldRef.current = 'expiry';
              }}
              onBlur={() => {
                // Delay clearing active field to allow focus transition to next field
                blurTimeoutRef.current = setTimeout(() => {
                  if (activeFieldRef.current === 'expiry') {
                    activeFieldRef.current = null;
                  }
                }, 100);
              }}
            />
          </div>
          {cardExpiryError && <div className="text-xs text-red-500">{cardExpiryError}</div>}
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <div
            className={cn(inputStyles, 'w-full p-0 pl-2', cardCvcError && 'border-red-500')}
            data-testid="stripe-card-cvc-wrapper"
          >
            <CardCvcElement
              options={{ style: { base: { fontSize: '14px', padding: '8px 12px', lineHeight: '44px' } } }}
              onChange={(e) => {
                setCardCvcValid(e.complete);
                setCardCvcError(getStripeErrorMessage(t, e.error || null, 'cvc'));
              }}
              onReady={(el) => {
                cvcElementRef.current = el;
              }}
              onFocus={() => {
                if (blurTimeoutRef.current) {
                  clearTimeout(blurTimeoutRef.current);
                  blurTimeoutRef.current = null;
                }
                activeFieldRef.current = 'cvc';
              }}
              onBlur={() => {
                // Delay clearing active field to allow focus transition to next field
                blurTimeoutRef.current = setTimeout(() => {
                  if (activeFieldRef.current === 'cvc') {
                    activeFieldRef.current = null;
                  }
                }, 100);
              }}
            />
          </div>
          {cardCvcError && <div className="text-xs text-red-500">{cardCvcError}</div>}
        </div>
      </div>
      <InputTaxComponent onValidate={onTaxValidationChange} />
    </div>
  );
};

export const StripeCardForm = () => {
  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({ paymentMethod: PaymentMethodEnum.CREDIT_CARD });
  const cardElementRef = useRef<StripeCardNumberElement | null>(null);
  const { pspLocale } = getLocale();

  if (isStripeLoading || !stripe) {
    return <Loader />;
  }
  return (
    <Elements
      stripe={stripe}
      options={{ locale: pspLocale as StripeElementLocale, appearance: STRIPE_APPEARANCE, loader: 'never' }}
    >
      <NewCardFormContent cardElementRef={cardElementRef as RefObject<StripeCardNumberElement | null>} />
    </Elements>
  );
};
